'use client';
import { jsx, Fragment } from '@emotion/react/jsx-runtime';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useLatestRef } from '@s7/hooks';
import { abs } from '@s7/utils';
import { locationCouldBeScrolled, handleScroll } from './utils/handleScroll.js';
import { getRootShadowHost } from './utils/getRootShadowHost.js';
import { RemoveScrollBarGlobal } from './RemoveScrollBarGlobal/RemoveScrollBarGlobal.js';
import { passiveListenerOptions } from './utils/passiveListenerOptions.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const getTouchXY = (event)=>'changedTouches' in event ? [
        event.changedTouches[0].clientX,
        event.changedTouches[0].clientY
    ] : [
        0,
        0
    ];
const getDeltaXY = (event)=>[
        event.deltaX,
        event.deltaY
    ];
const deltaCompare = (x, y)=>x[0] === y[0] && x[1] === y[1];
const addListener = (type, handler)=>document.addEventListener(type, handler, passiveListenerOptions);
const removeListener = (type, handler)=>document.removeEventListener(type, handler, passiveListenerOptions);
let lockStack = [];
const RemoveScrollbar = (props)=>{
    const { lockRef, removeScrollBar, setCallbacks, noIsolation } = props, others = _object_without_properties_loose(props, [
        "lockRef",
        "removeScrollBar",
        "setCallbacks",
        "noIsolation"
    ]);
    const [lock] = useState({});
    const shouldPreventQueue = useRef([]);
    const touchStartRef = useRef([
        0,
        0
    ]);
    const activeAxis = useRef();
    const noIsolationRef = useLatestRef(noIsolation);
    const shouldCancelEvent = useCallback((event, parent)=>{
        if ('touches' in event && event.touches.length === 2 || event.type === 'wheel' && event.ctrlKey) {
            return true;
        }
        const touch = getTouchXY(event);
        const touchStart = touchStartRef.current;
        const deltaX = 'deltaX' in event ? event.deltaX : touchStart[0] - touch[0];
        const deltaY = 'deltaY' in event ? event.deltaY : touchStart[1] - touch[1];
        let currentAxis;
        const target = event.target;
        const moveDirection = abs(deltaX) > abs(deltaY) ? 'h' : 'v';
        /*
             * Позволяем отслеживание touch по горизонтали в range-input.
             * Они не вызывают прокрутки страницы.
             * */ if ('touches' in event && moveDirection === 'h' && target.type === 'range') {
            return false;
        }
        let canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
        if (!canBeScrolledInMainDirection) {
            return true;
        }
        if (canBeScrolledInMainDirection) {
            currentAxis = moveDirection;
        } else {
            currentAxis = moveDirection === 'v' ? 'h' : 'v';
            canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
        }
        if (!canBeScrolledInMainDirection) {
            return false;
        }
        if (!activeAxis.current && 'changedTouches' in event && (deltaX || deltaY)) {
            activeAxis.current = currentAxis;
        }
        if (!currentAxis) {
            return true;
        }
        const cancelingAxis = activeAxis.current || currentAxis;
        return handleScroll(cancelingAxis, parent, event, cancelingAxis === 'h' ? deltaX : deltaY, true);
    }, []);
    const shouldPrevent = useCallback((_event)=>{
        const event = _event;
        if (!lockStack.length || lockStack.at(-1) !== lock) {
            return;
        }
        const delta = 'deltaY' in event ? getDeltaXY(event) : getTouchXY(event);
        const sourceEvent = shouldPreventQueue.current.filter((currentEvent)=>currentEvent.name === event.type && (currentEvent.target === event.target || event.target === currentEvent.shadowParent) && deltaCompare(currentEvent.delta, delta))[0];
        if (sourceEvent && sourceEvent.should) {
            if (event.cancelable) {
                event.preventDefault();
            }
            return;
        }
        if (!sourceEvent && !noIsolationRef.current && event.cancelable) {
            event.preventDefault();
        }
    }, []);
    const shouldCancel = useCallback((name, delta, target, should)=>{
        const event = {
            name,
            delta,
            target,
            should,
            shadowParent: getRootShadowHost(target)
        };
        shouldPreventQueue.current.push(event);
        setTimeout(()=>{
            shouldPreventQueue.current = shouldPreventQueue.current.filter((currentEvent)=>currentEvent !== event);
        }, 1);
    }, []);
    const scrollTouchStart = useCallback((event)=>{
        touchStartRef.current = getTouchXY(event);
        activeAxis.current = undefined;
    }, []);
    const scrollWheel = useCallback((event)=>{
        shouldCancel(event.type, getDeltaXY(event), event.target, shouldCancelEvent(event, lockRef == null ? void 0 : lockRef.current));
    }, []);
    const scrollTouchMove = useCallback((event)=>{
        shouldCancel(event.type, getTouchXY(event), event.target, shouldCancelEvent(event, lockRef == null ? void 0 : lockRef.current));
    }, []);
    useEffect(()=>{
        lockStack.push(lock);
        setCallbacks == null ? void 0 : setCallbacks({
            onScrollCapture: scrollWheel,
            onWheelCapture: scrollWheel,
            onTouchMoveCapture: scrollTouchMove
        });
        addListener('wheel', shouldPrevent);
        addListener('touchmove', shouldPrevent);
        addListener('touchstart', scrollTouchStart);
        return ()=>{
            lockStack = lockStack.filter((inst)=>inst !== lock);
            removeListener('wheel', shouldPrevent);
            removeListener('touchmove', shouldPrevent);
            removeListener('touchstart', scrollTouchStart);
        };
    }, []);
    return /*#__PURE__*/ jsx(Fragment, {
        children: removeScrollBar && /*#__PURE__*/ jsx(RemoveScrollBarGlobal, _extends({}, others))
    });
};

export { RemoveScrollbar, getDeltaXY, getTouchXY };
//# sourceMappingURL=RemoveScrollbar.js.map
